/**
 * Nav group with children. Expanded, an inline non-interactive header with
 * children always rendered below it (unchanged from design_handoff_sidebar_nav
 * — groups are never a toggle). Collapsed, the header becomes a 40x40 icon
 * button that opens a NavFlyout on click.
 * Spec: assets/handoffs/design_handoff_app_shell (component table, Rules —
 * collapse, Collapsed — flyout frame).
 *
 * @param {string} label
 * @param {string} icon
 * @param {Array<{key,label,to}>} children
 * @param {string=} activeKey
 * @param {boolean=false} collapsed
 * @param {function=} onNavigate
 */
import React, { useLayoutEffect, useState } from "react";
import { createPortal } from "react-dom";
import classNames from "classnames";
import Icon from "../../../theme/untitled/icons/Icon";
import SidebarNavItem from "../SidebarNavItem/SidebarNavItem";
import SidebarTooltip from "../SidebarTooltip/SidebarTooltip";
import NavFlyout from "../NavFlyout/NavFlyout";
import { useDismissablePanel } from "../useDismissablePanel";
import styles from "../../PageTemplate/SidebarNav.module.scss";
import flyoutWrapStyles from "./SidebarNavParent.module.scss";

const SidebarNavParent = ({
  label,
  icon,
  children = [],
  activeKey,
  collapsed = false,
  onNavigate,
}) => {
  const hasActiveChild = children.some((c) => c.key === activeKey);
  const { open, setOpen, triggerRef, panelRef, close } = useDismissablePanel();
  const [flyoutPosition, setFlyoutPosition] = useState(null);

  // The trigger lives inside Sidebar's `.nav`, which scrolls (overflow-y:
  // auto) — the UA computes that element's overflow-x as auto too, clipping
  // a plain `position: absolute` flyout the moment it overflows
  // horizontally. Portal the flyout to <body> and position it in viewport
  // coordinates instead, so it escapes that clip. Re-measured on scroll and
  // resize so it tracks the trigger while open.
  useLayoutEffect(() => {
    if (!open) return;

    const measure = () => {
      const rect = triggerRef.current?.getBoundingClientRect();
      if (rect) setFlyoutPosition({ top: rect.top, left: rect.right + 6 });
    };

    measure();
    window.addEventListener("scroll", measure, true);
    window.addEventListener("resize", measure);
    return () => {
      window.removeEventListener("scroll", measure, true);
      window.removeEventListener("resize", measure);
    };
  }, [open, triggerRef]);

  if (!collapsed) {
    return (
      <li className={styles.group}>
        <div
          className={classNames(styles.row, styles.groupHeader, {
            [styles["groupHeader--parentOfCurrent"]]: hasActiveChild,
          })}
        >
          <Icon name={icon} className={styles.icon} />
          <span className={styles.label}>{label}</span>
        </div>
        <ul className={styles.groupChildren}>
          {children.map((child) => (
            <li key={child.key}>
              <SidebarNavItem
                label={child.label}
                to={child.to}
                active={child.key === activeKey}
                child
              />
            </li>
          ))}
        </ul>
      </li>
    );
  }

  const trigger = (
    <button
      ref={triggerRef}
      type="button"
      className={classNames(styles.row, styles["row--collapsed"], {
        [styles["row--parentOpen"]]: open,
        [styles["row--current"]]: hasActiveChild && !open,
      })}
      aria-haspopup="menu"
      aria-expanded={open}
      onClick={() => setOpen((o) => !o)}
    >
      <Icon name={icon} className={styles.icon} />
    </button>
  );

  return (
    <li className={flyoutWrapStyles.wrap}>
      {open ? trigger : <SidebarTooltip label={label}>{trigger}</SidebarTooltip>}
      {open &&
        flyoutPosition &&
        createPortal(
          <div
            ref={panelRef}
            // 999999, not 30: on builder pages (CalendarPage/AlertPage) this
            // flyout sits right next to the widget preview, whose own design
            // stylesheet can set an arbitrarily high z-index on its content
            // (e.g. the alert widget uses 19999) — see SidebarTooltip.module.scss.
            style={{ position: "fixed", top: flyoutPosition.top, left: flyoutPosition.left, zIndex: 999999 }}
          >
            <NavFlyout
              parentLabel={label}
              children={children}
              activeKey={activeKey}
              onNavigate={(key) => {
                close(false);
                onNavigate?.(key);
              }}
            />
          </div>,
          document.body
        )}
    </li>
  );
};

export default SidebarNavParent;
